import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "../hall_1/hallTimer";
import {
  markHall2HotspotCompleted,
  unlockHall2Hotspot,
  getRedirectIfHall2StageDone,
} from "./heritageHallData";
import {
  MASTERPIECE_ARTWORK,
  MASTERPIECE_CLUE,
  MASTERPIECE_POINTS,
  MASTERPIECE_SUCCESS_COPY,
  MASTERPIECE_UNLOCKS,
} from "./masterpieceData";
import MasterpieceJigsaw from "./MasterpieceJigsaw";
import deskBg from "./stage7/stage7_desk.jpg";
import mobBg from "./stage7/stage7_mob.jpg";
import letterBg from "../img/letter.png";
import greenBtn from "../img/green_button.png";
import "../fonts/breuer-headline.css";
import "../game/stageSuccessLetter.css";
import "./stage7.css";

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

/** Hall 2 Stage 7 — Restore the Masterpiece (Bharat Mata jigsaw). */
export default function HallStage7() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const [status, setStatus] = useState("playing");
  const [errorMsg, setErrorMsg] = useState("");
  const [skipPopup, setSkipPopup] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [correctPieces, setCorrectPieces] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const elapsedRef = useRef(0);
  const completingRef = useRef(false);

  const liveStagePoints = useMemo(
    () =>
      liveProgressPoints(
        correctPieces,
        MASTERPIECE_ARTWORK.pieceCount,
        MASTERPIECE_POINTS
      ),
    [correctPieces]
  );

  const displayPoints =
    status === "success" ? totalScore : totalScore + liveStagePoints;

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-2"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall2-stage7-page");
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground: deskBg,
      mobForeground: mobBg,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall2-stage7-page");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const redirect = getRedirectIfHall2StageDone(stage, 7);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || status === "success") return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, status]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (status === "playing") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    status,
  ]);

  const finishStage = useCallback(
    async ({ score, skipped = false }) => {
      if (completingRef.current || status === "success" || status === "saving") {
        return;
      }
      completingRef.current = true;
      setStatus("saving");
      setErrorMsg("");

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const answers = {
        puzzle: "restore_masterpiece",
        artwork: MASTERPIECE_ARTWORK.id,
        pieceCount: MASTERPIECE_ARTWORK.pieceCount,
        skipped: Boolean(skipped),
      };

      try {
        let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
        if (!stageId && !isDemoBypass && backendBase && adminToken) {
          stageId = await ensureStageRowId({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
          });
          if (stageId) setStageRowId(String(stageId));
        }

        const stageScore = Number(score) || 0;

        if (!isDemoBypass && backendBase && adminToken && stageId) {
          const saveResult = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId,
            stageNumber: 7,
            score: stageScore,
            answers,
            time: timeStr,
          });
          if (!saveResult?.ok) {
            throw new Error("stage7 save failed");
          }
          if (saveResult.total_score != null) {
            setTotalScore(Number(saveResult.total_score) || 0);
          } else {
            setTotalScore((prev) => prev + stageScore);
          }
        } else {
          setTotalScore((prev) => prev + stageScore);
        }

        markHall2HotspotCompleted("restore_masterpiece");
        unlockHall2Hotspot(MASTERPIECE_UNLOCKS);

        if (skipped) {
          navigate("/hall-2", { replace: true });
          return;
        }

        setStatus("success");
      } catch (err) {
        console.error("Masterpiece save:", err);
        completingRef.current = false;
        setStatus("playing");
        setErrorMsg(
          skipped
            ? "Could not skip stage. Please try again."
            : "Could not save progress. Please try again."
        );
      }
    },
    [
      status,
      stageRowId,
      isDemoBypass,
      backendBase,
      adminToken,
      storedUser,
      navigate,
    ]
  );

  const completePuzzle = useCallback(() => {
    finishStage({ score: MASTERPIECE_POINTS, skipped: false });
  }, [finishStage]);

  const skipStage = useCallback(() => {
    if (status !== "playing") return;
    setSkipPopup(true);
  }, [status]);

  const dismissSkipPopup = useCallback(() => {
    if (status === "saving") return;
    setSkipPopup(false);
  }, [status]);

  const confirmSkipStage = useCallback(() => {
    if (status !== "playing") return;
    setSkipPopup(false);
    finishStage({ score: 0, skipped: true });
  }, [status, finishStage]);

  if (status === "success") {
    return (
      <div className="hall2-stage7 masterpiece-success">
        <div className="stage-escape-hud">
          <StageTimer
            timeLabel={formatSecondsToClock(elapsedSec)}
            points={displayPoints}
          />
        </div>

     

        <div className="masterpiece-success__layout">
          <aside
            className="masterpiece-success__box stage-success-letter"
            style={{ backgroundImage: `url(${letterBg})` }}
          >
            <h2 className="masterpiece-success__title">
              {MASTERPIECE_SUCCESS_COPY.title}
            </h2>
            <div className="masterpiece-success__rule" aria-hidden>
              <span />
            </div>
            <p className="masterpiece-success__body">
              {MASTERPIECE_SUCCESS_COPY.body}
            </p>
            <div className="masterpiece-success__rule" aria-hidden>
              <span />
            </div>
            <p className="masterpiece-success__clue-label">
              {MASTERPIECE_SUCCESS_COPY.clueLabel}
            </p>
            <p className="masterpiece-success__clue">{MASTERPIECE_CLUE}</p>

            <button
              type="button"
              className="masterpiece-success__cta"
              style={{ backgroundImage: `url(${greenBtn})` }}
              onClick={() => navigate("/hall-2")}
            >
              CONTINUE
            </button>
          </aside>

          <div
            className="masterpiece-success__frame"
            style={{ backgroundImage: `url(${MASTERPIECE_ARTWORK.frame})` }}
            aria-hidden
          >
            <img
              src={MASTERPIECE_ARTWORK.src}
              alt=""
              className="masterpiece-success__art"
              draggable={false}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hall2-stage7 masterpiece-play">
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      <div className="masterpiece-play__inner">
        <header className="masterpiece-play__header">
          <h1 className="masterpiece-play__title">RESTORE THE MASTERPIECE</h1>
          <div className="masterpiece-play__rule" aria-hidden>
            <span />
          </div>
          <p className="masterpiece-play__hint">
          Piece the artwork back together to restore the gallery's centrepiece.
          </p>
          <p className="masterpiece-play__credit">
            {MASTERPIECE_ARTWORK.title}
            {MASTERPIECE_ARTWORK.artist
              ? ` — ${MASTERPIECE_ARTWORK.artist}`
              : ""}
          </p>
        </header>

        <MasterpieceJigsaw
          image={MASTERPIECE_ARTWORK.src}
          frame={MASTERPIECE_ARTWORK.frame}
          pieceCount={MASTERPIECE_ARTWORK.pieceCount}
          disabled={status === "saving" || skipPopup}
          onComplete={completePuzzle}
          onCorrectPieceCountChange={setCorrectPieces}
        />

        <div className="masterpiece-play__actions">
          <button
            type="button"
            className="masterpiece-play__skip"
            onClick={skipStage}
            disabled={status === "saving" || skipPopup}
          >
            SKIP STAGE
          </button>
          <span className="masterpiece-play__skip-note">0 pts if skipped</span>
        </div>

        {errorMsg ? (
          <p className="masterpiece-play__error" role="alert">
            {errorMsg}
          </p>
        ) : null}

        {status === "saving" ? (
          <p className="masterpiece-play__saving" aria-live="polite">
            Restoring masterpiece…
          </p>
        ) : null}
      </div>

      {skipPopup ? (
        <div className="masterpiece-skip" role="dialog" aria-modal="true">
          <div
            className="masterpiece-skip__veil"
            aria-hidden
            onClick={dismissSkipPopup}
          />
          <div className="masterpiece-skip__card">
            <h2 className="masterpiece-skip__title">Skip this stage?</h2>
            <p className="masterpiece-skip__text">
              You will earn 0 points for Restore the Masterpiece.
            </p>
            <div className="masterpiece-skip__actions">
              <button
                type="button"
                className="masterpiece-skip__btn masterpiece-skip__btn--ghost"
                onClick={dismissSkipPopup}
                disabled={status === "saving"}
              >
                CANCEL
              </button>
              <button
                type="button"
                className="masterpiece-skip__btn"
                onClick={confirmSkipStage}
                disabled={status === "saving"}
              >
                SKIP
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
